/**
 * Canal temps réel du Mode Debug (admin).
 *
 * Le serveur pousse chaque événement (`debug.event`), un instantané agrégé
 * toutes les deux secondes (`debug.snapshot`), l'état de session
 * (`debug.status`) et la fin de session (`debug.ended`) — le dashboard ne
 * recharge jamais la page. Reconnexion à recul progressif, sauf après une
 * fermeture volontaire du serveur (code 4410 : session terminée).
 *
 * Le jeton part en sous-protocole WebSocket, comme le canal de sonnerie :
 * il ne finit pas dans les journaux d'accès du proxy.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API, getToken } from "@/lib/crmApi";

const WS_BASE = API.replace(/^http/, "ws");
const RETRY_MS = [1000, 2000, 4000, 8000, 15000];
const MAX_EVENTS = 400;
const CLOSE_ENDED = 4410;

export default function useDebugSocket({ enabled = true } = {}) {
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [events, setEvents] = useState([]);
  const [ended, setEnded] = useState(null);
  const socketRef = useRef(null);
  const attemptRef = useRef(0);
  const closedRef = useRef(false);

  const clear = useCallback(() => { setEvents([]); setSnapshot(null); setEnded(null); }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    closedRef.current = false;
    let timer = null;
    let ping = null;

    const open = () => {
      if (closedRef.current) return;
      const token = getToken();
      if (!token) { timer = setTimeout(open, 2000); return; }
      const ws = new WebSocket(`${WS_BASE}/admin/debug/ws`, [token]);
      socketRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
        ping = setInterval(() => { try { ws.send("ping"); } catch { /* fermé */ } }, 25000);
      };

      ws.onmessage = (event) => {
        let message;
        try { message = JSON.parse(event.data); } catch { return; }
        const { type, data } = message;
        if (type === "debug.status" || type === "pong") {
          setStatus(data);
          if (data && !data.active) setSnapshot(null);
        } else if (type === "debug.snapshot") {
          setSnapshot(data);
          if (data?.session) setStatus((prev) => ({ ...(prev || {}), active: true, session: data.session }));
        } else if (type === "debug.event") {
          setEvents((prev) => [...prev, data].slice(-MAX_EVENTS));
        } else if (type === "debug.ended") {
          setEnded(data);
          setStatus((prev) => ({ ...(prev || {}), active: false, session: null }));
          setSnapshot(null);
        }
      };

      ws.onclose = (e) => {
        setConnected(false);
        socketRef.current = null;
        if (ping) { clearInterval(ping); ping = null; }
        if (closedRef.current) return;
        // Session terminée par le serveur : on se reconnecte quand même (le
        // dashboard doit pouvoir relancer une session), mais sans précipitation.
        const delay = e?.code === CLOSE_ENDED ? 1500
          : RETRY_MS[Math.min(attemptRef.current, RETRY_MS.length - 1)];
        attemptRef.current += 1;
        timer = setTimeout(open, delay);
      };

      ws.onerror = () => { try { ws.close(); } catch { /* déjà fermé */ } };
    };

    open();
    return () => {
      closedRef.current = true;
      if (timer) clearTimeout(timer);
      if (ping) clearInterval(ping);
      try { socketRef.current?.close(); } catch { /* déjà fermé */ }
      socketRef.current = null;
    };
  }, [enabled]);

  return { connected, status, snapshot, events, ended, clear, setStatus, setSnapshot };
}
